import React, { useState, useEffect, useRef } from 'react';
import Styles from './styles.module.css';
import Loader from '../../assets/svg/loader.svg';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Auswählen...",
    searchPlaceholder = "Suchen...",
    isLoading = false,
    disabled = false,
    displayKey = 'name',
    valueKey = 'id',
    renderOption = null,
    className = '',
    zIndex = 1000 // Allow parent to override z-index
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Update filtered options when options or search term changes
    useEffect(() => {
        if (!searchTerm) {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option => {
                const displayValue = option[displayKey] || '';
                return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setFilteredOptions(filtered);
        }
    }, [options, searchTerm, displayKey]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Get display value for selected option
    const getDisplayValue = () => {
        if (!value) return '';
        const selectedOption = options.find(option => option[valueKey] === value);
        return selectedOption ? selectedOption[displayKey] : '';
    };

    const handleToggle = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    const handleOptionSelect = (option) => {
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    return (
        <div className={`${Styles.container} ${className}`} ref={dropdownRef} style={{ zIndex: isOpen ? zIndex : 'auto' }}>
            <div 
                className={`${Styles.selectBox} ${isOpen ? Styles.open : ''} ${disabled ? Styles.disabled : ''}`}
                onClick={handleToggle}
            >
                <span className={Styles.selectedValue}>
                    {getDisplayValue() || placeholder}
                </span>
                <span className={`${Styles.arrow} ${isOpen ? Styles.up : Styles.down}`}>
                    ▼
                </span>
            </div>

            {isOpen && (
                <div className={Styles.dropdown} style={{ zIndex: zIndex }}>
                    <div className={Styles.searchContainer}>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={handleKeyDown}
                            className={Styles.searchInput}
                            autoComplete="off"
                        />
                    </div>

                    <div className={Styles.optionsList}>
                        {isLoading ? (
                            <div className={Styles.loading}>
                                <img src={Loader} width={16} height={16} alt="Loading" />
                                <span>Laden...</span>
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className={Styles.noResults}>
                                {searchTerm ? 'Keine Ergebnisse gefunden' : 'Keine Optionen verfügbar'}
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option[valueKey]}
                                    className={`${Styles.option} ${
                                        value === option[valueKey] ? Styles.selected : ''
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    {renderOption ? renderOption(option) : (
                                        <span>{option[displayKey]}</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;